import type { ReactNode } from "react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@rosette/ts/src/util/cn.js";
import { useAccessibility } from "@rosette/web_components/src/accessibility/AccessibilityContext.js";
import { SheenScheduler } from "@/components/generic/sheen/SheenScheduler.js";


// The scheduler needs the sweep's duration, so it is set from here on each
// sweep, overriding the one in --animate-shine-once.
export const SHEEN_DURATION_MS = 1500;


/**
 * Runs sheens on the SheenScheduler's timing, while enabled and unless the
 * user prefers reduced motion. Call trigger when the pointer enters or
 * leaves. isClearFor answers SheenScheduler.isClearFor, and is true while
 * no sheens run.
 */
export function useSheen(enabled: boolean): {
    sheenCount: number;
    trigger: () => void;
    isClearFor: (durationMs: number) => boolean;
} {
    const reducedMotion = useAccessibility().useReducedMotion();
    const [sheenCount, setSheenCount] = useState(0);
    const schedulerRef = useRef<SheenScheduler | null>(null);

    useEffect(() => {
        if (reducedMotion || !enabled)
            return;

        const scheduler = new SheenScheduler(SHEEN_DURATION_MS, () => setSheenCount(count => count + 1));
        schedulerRef.current = scheduler;
        scheduler.begin();
        return () => {
            scheduler.stop();
            schedulerRef.current = null;
        };
    }, [reducedMotion, enabled]);

    const trigger = useCallback(() => schedulerRef.current?.trigger(), []);
    const isClearFor = useCallback(
        (durationMs: number) => schedulerRef.current?.isClearFor(durationMs) ?? true,
        [],
    );
    return { sheenCount, trigger, isClearFor };
}


interface SheenTextProps {
    sheenCount: number;
    sheenClassName: string;
    children: ReactNode;
}

/**
 * Text with the win banner's shine: a lighter copy of the text, clipped
 * to a diagonal band that sweeps across it once per sheen.
 */
export function SheenText({ sheenCount, sheenClassName, children }: SheenTextProps) {
    return (
        <span className="relative inline-block">
            {children}
            {sheenCount > 0 && (
                <span
                    key={sheenCount}
                    aria-hidden={true}
                    className={cn(
                        "animate-shine-once motion-reduce:hidden absolute left-0 top-0",
                        sheenClassName,
                    )}
                    style={{ animationDuration: `${SHEEN_DURATION_MS}ms` }}>

                    {children}
                </span>
            )}
        </span>
    );
}
